import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CONFIG } from '../config/variables';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiCheckCircle } = FiIcons;

const InvestmentCalculator = () => {
  const [selectedDuration, setSelectedDuration] = useState(12);
  const durations = [
    { months: 6, label: '6 months' },
    { months: 12, label: '12 months' },
    { months: 24, label: '24 months' }
  ];

  const calculateCost = (durationMonths) => {
    const paidMonths = Math.max(0, durationMonths - CONFIG.ONGOING_STARTS_AFTER_MONTHS_FREE);
    const total = CONFIG.PACKAGE_PRICE_ONETIME + (CONFIG.ONGOING_PRICE_MONTHLY * paidMonths);
    const monthlyEquivalent = total / durationMonths;
    const weeklyEquivalent = total / (durationMonths * 4.333);
    return { total, monthlyEquivalent, weeklyEquivalent };
  };

  const scrollToPayment = () => {
    const element = document.getElementById('payment');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const { total, monthlyEquivalent, weeklyEquivalent } = calculateCost(selectedDuration);

  return (
    <section className="py-20 bg-gradient-to-br from-blue-900/20 to-purple-900/20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            What does this really cost?
          </h2>
        </motion.div>

        {/* Investment Timeline Visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 lg:p-8">
            <h3 className="text-white font-bold mb-6 text-center">Year 1 Ownership Timeline</h3>
            
            {/* Desktop Timeline */}
            <div className="hidden md:block mb-8">
              <div className="relative h-4 bg-white/10 rounded-full overflow-hidden flex">
                <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600" style={{ width: '25%' }}></div>
                <div className="h-full bg-white/5" style={{ width: '75%' }}></div>
              </div>
              <div className="flex justify-between mt-3 text-[10px] text-white/40 font-bold uppercase tracking-wider">
                <span>Month 1</span>
                <span>Month 3</span>
                <span>Month 6</span>
                <span>Month 9</span>
                <span>Month 12</span>
              </div>
              <div className="grid grid-cols-4 mt-2 h-8">
                <div className="col-span-1 border-x border-dashed border-white/20 flex items-center justify-center">
                  <span className="text-blue-300 text-[11px] font-bold">Included: Hosting + Email</span>
                </div>
                <div className="col-span-3 border-r border-dashed border-white/20 flex items-center justify-center">
                  <span className="text-white/40 text-[11px] font-bold">$20/mo ongoing</span>
                </div>
              </div>
            </div>

            {/* Mobile Timeline */}
            <div className="md:hidden space-y-3 mb-8">
              <div className="bg-blue-500/20 border border-blue-500/30 p-4 rounded-xl flex items-center justify-between">
                <span className="text-white font-bold">Months 1 to 3</span>
                <span className="text-blue-400 font-bold">Included</span>
              </div>
              <div className="bg-white/5 border border-white/10 p-4 rounded-xl flex items-center justify-between">
                <span className="text-white font-bold">Months 4 to 12</span>
                <span className="text-white/60">$20/mo</span>
              </div>
            </div>

            <div className="flex flex-col items-center mb-8">
              <div className="bg-white/10 px-6 py-2 rounded-full border border-white/20 mb-6">
                <span className="text-white font-bold">Year 1 total: $897 + ($20 x 9) = </span>
                <span className="text-blue-400 font-bold">$1,077</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
                {[
                  { label: "Year 1 average", value: "$89.75 /mo" },
                  { label: "Weekly equivalent", value: "$20.70 /wk" },
                  { label: "ROI Potential", value: "One job covers it" }
                ].map((chip, i) => (
                  <div key={i} className="bg-white/5 p-4 rounded-xl border border-white/10 text-center">
                    <p className="text-white/40 text-[10px] uppercase font-bold mb-1">{chip.label}</p>
                    <p className="text-white font-bold">{chip.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: 0.1 }}
          viewport={{ once: true }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8"
        >
          {/* Duration Toggle */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {durations.map((duration) => (
              <button
                key={duration.months}
                onClick={() => setSelectedDuration(duration.months)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  selectedDuration === duration.months
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                    : 'bg-white/10 text-white/80 hover:bg-white/20'
                }`}
              >
                {duration.label}
              </button>
            ))}
          </div>

          {/* Cost Breakdown */}
          <div className="text-center space-y-6">
            <div>
              <p className="text-white/60 mb-2">Total investment over {selectedDuration} months</p>
              <p className="text-4xl font-bold text-white">${total.toLocaleString()}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                <p className="text-white/60 mb-1">Monthly equivalent</p>
                <p className="text-2xl font-bold text-white">${monthlyEquivalent.toFixed(0)}</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                <p className="text-white/60 mb-1">Weekly equivalent</p>
                <p className="text-2xl font-bold text-white">${weeklyEquivalent.toFixed(0)}</p>
              </div>
            </div>

            <button
              onClick={scrollToPayment}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-xl"
            >
              Secure My Landing Page
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InvestmentCalculator;